import { FC, FormEvent, useMemo, useState } from "react";
import Select, { SingleValue } from "react-select";

export type IndicationValue = "DPN of the Foot" | "PHN" | "Other" | "";

type IndicationModalProps = {
  caseId: string | number;
  patientName: string;
  isOpen: boolean;
  onClose?: () => void;
  onSubmit: (value: IndicationValue) => void;
};

const IndicationModal: FC<IndicationModalProps> = ({
  caseId,
  patientName,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [value, setValue] = useState<IndicationValue>("");
  const options = useMemo(
    () => [
      { value: "DPN of the Foot", label: "Diabetic peripheral neuropathy of the feet (DPN)" },
      { value: "PHN", label: "Postherpetic polyneuropathy (PHN)" },
      { value: "Other", label: "Other" },
    ],
    []
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!value) return;
    setValue("");
    onSubmit(value);
  };

  return (
    <div
      id={`confirmed_indication-modal-window-${caseId}`}
      className={`modal fade ${isOpen ? "show show-modal" : ""}`}
      role="dialog"
      aria-labelledby="confirmed_indication-modal-title"
      aria-hidden={!isOpen}
      style={{ display: isOpen ? "block" : "none" }}
    >
      <div className="container-fluid">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div id="modal-confirmed_indication-header" className="modal-header">
              <h2 id="confirmed_indication-modal-title" className="mb-0">
                Confirm Indication For Case {caseId}
              </h2>
              {onClose ? (
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
              ) : null}
            </div>
            <div className="modal-body">
              <p>
                As part of your recent benefits investigation request for your patient, {patientName},
                we request confirmation of the indicated use of QUTENZA currently on file at MQC.
              </p>
              <p>
                Please note that auto-reverification of benefits has been paused pending this
                confirmation. You may update this value later by clicking on the case in your dashboard
                and clicking on "Flagged for HCP Review" in the indication section. Should you have any
                questions or need assistance, please don't hesitate to contact us.
              </p>
              <p>
                Thank you for your continued partnership and support.
              </p>

              <form id="confirmed_indication-form" onSubmit={handleSubmit}>
                <Select
                  classNamePrefix="react-select"
                  isClearable={false}
                  placeholder="Select an indication *"
                  options={options}
                  value={value ? options.find((option) => option.value === value) ?? null : null}
                  onChange={(option: SingleValue<{ value: string; label: string }>) => {
                    setValue(option?.value as IndicationValue ?? "");
                  }}
                />

                <button type="submit" className="btn btn-blue mt-3 float-end" disabled={!value}>
                  Save
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndicationModal;
